import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { Waves, Sun, Shell, MapPin, ArrowRight } from "lucide-react";

const beaches = [
  {
    name: "South Beach",
    description: "The most popular beach on Long Island, South Beach offers pristine sand and calm waters perfect for swimming. A short walk from Chestnut Hill Inn.",
    features: ["Swimming", "Sunbathing", "Calm waters", "Family-friendly"],
    distance: "5-minute walk",
    image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800"
  },
  {
    name: "Fowler Beach",
    description: "A quieter, more secluded beach ideal for those seeking peace and natural beauty. Rocky outcrops provide excellent tide pool exploration.",
    features: ["Tide pools", "Secluded", "Natural beauty", "Birdwatching"],
    distance: "10-minute walk",
    image: "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=800"
  },
  {
    name: "Wreck Cove",
    description: "Named for historical shipwrecks, this cove offers dramatic coastal views and is perfect for photography and peaceful contemplation.",
    features: ["Historic sites", "Photography", "Rocky shores", "Dramatic views"],
    distance: "15-minute walk",
    image: "https://images.unsplash.com/photo-1501426026826-31c667bdf23d?w=800"
  }
];

export default function BeachesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO
        title="Best Beaches on Long Island, Maine | Chestnut Hill Inn"
        description="Discover the beautiful beaches near Chestnut Hill Inn on Long Island, Maine. South Beach, Fowler Beach, and Wreck Cove are all within walking distance."
      />

      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about" className="text-sm text-[#78716C] hover:text-[#1C1917]">About</Link>
            <Link to="/beaches" className="text-sm text-[#1C1917] font-medium">Beaches</Link>
            <Link to="/getting-here" className="text-sm text-[#78716C] hover:text-[#1C1917]">Getting Here</Link>
            <Link to="/things-to-do" className="text-sm text-[#78716C] hover:text-[#1C1917]">Things to Do</Link>
            <Link to="/winter-retreats" className="text-sm text-[#78716C] hover:text-[#1C1917]">Winter Retreats</Link>
            <Link to="/booking">
              <Button className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white">
                Book Now
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920"
            alt="Long Island Maine Beach"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-6xl mx-auto px-6 pb-16 w-full">
            <div className="flex items-center gap-2 text-white/80 mb-4">
              <Waves size={20} />
              <span className="text-sm uppercase tracking-widest">Island Beaches</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-4">
              Best Beaches on<br />Long Island, Maine
            </h1>
            <p className="text-white/90 text-lg max-w-xl">
              Three beautiful beaches within walking distance of Chestnut Hill Inn
            </p>
          </div>
        </div>
      </section>

      {/* Beach Cards */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid gap-12">
            {beaches.map((beach, index) => (
              <div key={beach.name} className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={beach.image}
                      alt={beach.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="flex items-center gap-2 text-[#134E4A] mb-2">
                    <MapPin size={16} />
                    <span className="text-sm">{beach.distance}</span>
                  </div>
                  <h2 className="font-serif text-3xl text-[#1C1917] mb-4">{beach.name}</h2>
                  <p className="text-[#78716C] mb-6">{beach.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {beach.features.map((feature) => (
                      <span
                        key={feature}
                        className="px-3 py-1 bg-[#e6f2f1] text-[#134E4A] text-sm"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beach Tips */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-serif text-3xl text-[#1C1917] text-center mb-12">Beach Day Tips</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#e6f2f1] flex items-center justify-center">
                <Sun size={32} className="text-[#134E4A]" />
              </div>
              <h3 className="font-serif text-xl text-[#1C1917] mb-2">Best Time to Visit</h3>
              <p className="text-[#78716C]">
                Morning hours offer calmer waters and fewer crowds. Late afternoon provides beautiful sunset views.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#e6f2f1] flex items-center justify-center">
                <Shell size={32} className="text-[#134E4A]" />
              </div>
              <h3 className="font-serif text-xl text-[#1C1917] mb-2">What to Bring</h3>
              <p className="text-[#78716C]">
                Sunscreen, beach towels, water, and snacks. The island store has basics if you forget something.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#e6f2f1] flex items-center justify-center">
                <Waves size={32} className="text-[#134E4A]" />
              </div>
              <h3 className="font-serif text-xl text-[#1C1917] mb-2">Tide Awareness</h3>
              <p className="text-[#78716C]">
                Check tide schedules for the best beach experience. Low tide is perfect for tide pool exploration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#134E4A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl text-white mb-4">
            Stay Steps from the Beach
          </h2>
          <p className="text-white/80 mb-8">
            Book your stay at Chestnut Hill Inn and wake up to the sound of waves
          </p>
          <Link to="/booking">
            <Button className="bg-white text-[#134E4A] hover:bg-stone-100 px-8 py-6">
              Book Your Stay <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1917] text-white py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h3 className="font-serif text-2xl mb-2">Chestnut Hill Inn</h3>
          <p className="text-white/60 mb-4">209 Beach Ave, Long Island, ME</p>
          <div className="flex justify-center gap-4">
            <a
              href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/chestnuthillinn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
